import axios from 'axios';
import { BACKEND_URL } from '../config';

const API_URL = BACKEND_URL || 'http://localhost:8081';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add an interceptor to include the token in requests when available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post('/v1/api/login', { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('userRole', response.data.role);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Login failed. Please try again.' };
    }
  },
  
  signup: async (email, password, invitation_token) => {
    try {
      const response = await api.post('/v1/api/sign-up', { 
        email, 
        password, 
        invitation_token 
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('userRole', response.data.role);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Signup failed. Please try again.' };
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    sessionStorage.removeItem('selectedOrganization');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  getUserRole: () => {
    return localStorage.getItem('userRole');
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/v1/api/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to get current user' };
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/v1/api/forget-password', { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to request password reset' };
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/v1/api/reset-password', { 
        token, 
        new_password: newPassword 
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reset password' };
    }
  }
};

export const organizationService = {
  getAllOrganizations: async () => {
    try {
      const response = await api.get('/v1/api/my-organizations');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch organizations.' };
    }
  },

  setSelectedOrganization: (organization) => {
    sessionStorage.setItem('selectedOrganization', JSON.stringify(organization));
  },

  getSelectedOrganization: () => {
    const org = sessionStorage.getItem('selectedOrganization');
    return org ? JSON.parse(org) : null;
  },

  clearSelectedOrganization: () => {
    sessionStorage.removeItem('selectedOrganization');
  }
};

export const usersService = {
  getAllUsers: async () => {
    try {
      const response = await api.get('/v1/api/users');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch users.' };
    }
  },

  inviteUser: async (email, role) => {
    try {
      const response = await api.post('/v1/api/invite', { email, role });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to send invitation.' };
    }
  }
};

export const callsService = {
  getCallAnalytics: async (fromDate = null, toDate = null) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const params = {
        organization_id: selectedOrg?.id
      };
      
      // Add date filters if provided
      if (fromDate) {
        params.from_date = fromDate;
      }
      if (toDate) {
        params.to_date = toDate;
      }
      
      const response = await api.get('/v1/api/call-analytics', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch call analytics.' };
    }
  },

  getCallLogs: async (page = 1, pageSize = 10, fromDate = null, toDate = null) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const params = {
        organization_id: selectedOrg?.id,
        page,
        page_size: pageSize
      };
      
      // Add date filters if provided
      if (fromDate) {
        params.from_date = fromDate;
      }
      if (toDate) {
        params.to_date = toDate;
      }
      
      const response = await api.get('/v1/api/logs', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch call logs.' };
    }
  },

  getCallDetails: async (callId, organizationId = null) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const orgId = organizationId || selectedOrg?.id;
      const response = await api.get('/v1/api/call', {
        params: {
          call_id: callId,
          organization_id: orgId
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch call details.' };
    }
  },

  scheduleCallBatch: async (earliestCallTime, latestCallTime, file, campaignName = null) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const formData = new FormData();
      formData.append('earliest_call_time', earliestCallTime);
      formData.append('latest_call_time', latestCallTime);
      formData.append('file', file);
      if (campaignName) {
        formData.append('campaign_name', campaignName);
      }

      const response = await api.post('/v1/api/call-scheduler', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to schedule call batch.' };
    }
  },

  downloadCallData: async () => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/download-call-data', {
        responseType: 'blob',
        params: {
          organization_id: selectedOrg?.id
        }
      });
      
      // Extract filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'call_data.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download call data.' };
    }
  },

  refreshCalls: async () => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/refresh-calls', {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to refresh calls data.' };
    }
  },

  callTest: async (phoneNumber, assistantVariables = null, whatsappVariables = null, whatsappTemplateName = null, callScheduleMinutes = 30, phoneType = 'sip', countryCode = '+91') => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const requestData = {
        phone_number: phoneNumber,
        assistant_variables: assistantVariables,
        whatsapp_variables: whatsappVariables,
        whatsapp_template_name: whatsappTemplateName,
        call_schedule_minutes: callScheduleMinutes,
        phone_type: phoneType,
        country_code: countryCode
      };
      
      const response = await api.post('/v1/api/call-test', requestData, {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to initiate test call.' };
    }
  },

  callAgain: async (callId) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const requestData = {
        call_id: callId
      };
      
      const response = await api.post('/v1/api/call-again', requestData, {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to call again.' };
    }
  }
};

export const analyticsService = {
  getAnalytics: async (dateRange = 'last15') => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/analytics', {
        params: {
          organization_id: selectedOrg?.id,
          date_range: dateRange
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch analytics.' };
    }
  }
};

export const batchesService = {
  createBatch: async (formData) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.post('/v1/api/batches', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { organization_id: selectedOrg?.id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create batch.' };
    }
  },

  getBatches: async () => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/batches', {
        params: { organization_id: selectedOrg?.id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch batches.' };
    }
  },

  getBatchById: async (batchId) => {
    try {
      const response = await api.get(`/v1/api/batches/${batchId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch batch details.' };
    }
  },

  getBatchCallDetails: async (batchId) => {
    try {
      const response = await api.get(`/v1/api/batch/${batchId}/call-details`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch batch call details.' };
    }
  },

  downloadBatchFailedCalls: async (batchId) => {
    try {
      const response = await api.get(`/v1/api/batch/${batchId}/failed-calls/download`, {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download failed calls.' };
    }
  },

  downloadBatchInterestedCalls: async (batchId) => {
    try {
      const response = await api.get(`/v1/api/batch/${batchId}/interested-calls/download`, {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to download interested calls.' };
    }
  },

  cancelBatch: async (batchId) => {
    try {
      const response = await api.post(`/v1/api/batches/${batchId}/cancel`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to cancel batch.' };
    }
  },

  checkAvailability: async (startTimeIso, taskCount) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/batches/availability', {
        params: {
          organization_id: selectedOrg?.id,
          start_time: startTimeIso,
          task_count: taskCount
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to check batch availability.' };
    }
  }
};

export const invitationService = {
  inviteUser: async (email, role, organization_id) => {
    try {
      const response = await api.post('/v1/api/invite', {
        email,
        role,
        organization_id
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to send invitation.' };
    }
  },

  getOrganizationMembers: async (organization_id) => {
    try {
      const response = await api.get(`/v1/api/organizations/${organization_id}/members`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch organization members.' };
    }
  },

  getAllUsers: async () => {
    try {
      const response = await api.get('/v1/api/users');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch users.' };
    }
  }
};

export const feedbackService = {
  // Create feedback for a call
  createFeedback: async (callId, feedbackData) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.post(`/v1/api/calls/${callId}/feedback`, feedbackData, {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create feedback.' };
    }
  },

  // Get feedback for a specific call
  getCallFeedback: async (callId, page = 1, pageSize = 10) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get(`/v1/api/calls/${callId}/feedback`, {
        params: {
          organization_id: selectedOrg?.id,
          page,
          page_size: pageSize
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch call feedback.' };
    }
  },

  // Update feedback (only by the user who created it)
  updateFeedback: async (feedbackId, feedbackData) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.put(`/v1/api/feedback/${feedbackId}`, feedbackData, {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update feedback.' };
    }
  },

  // Delete feedback (only by the user who created it)
  deleteFeedback: async (feedbackId) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.delete(`/v1/api/feedback/${feedbackId}`, {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete feedback.' };
    }
  },

  // Resolve/unresolve feedback (only super_admin)
  resolveFeedback: async (feedbackId, isResolved) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.patch(`/v1/api/feedback/${feedbackId}/resolve`, {
        is_resolved: isResolved
      }, {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to resolve feedback.' };
    }
  },

  // Get all feedback across all calls (admin+ only)
  getAllFeedback: async (page = 1, pageSize = 10, feedbackType = null, isResolved = null) => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const params = {
        organization_id: selectedOrg?.id,
        page,
        page_size: pageSize
      };
      if (feedbackType) params.feedback_type = feedbackType;
      if (isResolved !== null) params.is_resolved = isResolved;

      const response = await api.get('/v1/api/feedback/all', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch all feedback.' };
    }
  },

  // Get feedback analytics (admin+ only)
  getFeedbackAnalytics: async () => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/feedback/analytics', {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch feedback analytics.' };
    }
  }
};

export const campaignsService = {
  getCampaigns: async () => {
    try {
      const selectedOrg = organizationService.getSelectedOrganization();
      const response = await api.get('/v1/api/campaigns', {
        params: {
          organization_id: selectedOrg?.id
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch campaigns.' };
    }
  },

  getCampaignById: async (campaignId) => {
    try {
      const response = await api.get(`/v1/api/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch batch details.' };
    }
  },

  deleteCampaign: async (campaignId) => {
    try {
      const response = await api.delete(`/v1/api/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete campaign.' };
    }
  }
};

export default api; 